/**
 * Value Object TransactionId - représente un identifiant unique de transaction
 *
 * Règles métier:
 * - Généré automatiquement
 * - Unique et immuable
 * - Format: timestamp + random (ex: "1706000000000-abc123")
 */

export class TransactionId {
  private constructor(private readonly _value: string) {
    if (!_value || _value.trim().length === 0) {
      throw new Error('TransactionId cannot be empty');
    }
    Object.freeze(this);
  }

  /**
   * Génère un nouvel ID de transaction
   */
  static generate(): TransactionId {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return new TransactionId(`${timestamp}-${random}`);
  }

  /**
   * Crée une instance TransactionId à partir d'une valeur existante
   * (pour reconstitution depuis persistence)
   */
  static create(value: string): TransactionId {
    return new TransactionId(value);
  }

  get value(): string {
    return this._value;
  }

  /**
   * Compare deux TransactionId
   */
  equals(other: TransactionId): boolean {
    return this._value === other._value;
  }

  /**
   * Extrait le timestamp de création
   */
  getTimestamp(): number {
    const parts = this._value.split('-');
    return parseInt(parts[0], 10);
  }

  /**
   * Extrait la date de création
   */
  getDate(): Date {
    return new Date(this.getTimestamp());
  }

  /**
   * Sérialise pour persistence
   */
  toJSON(): string {
    return this._value;
  }

  /**
   * Désérialise depuis persistence
   */
  static fromJSON(value: string): TransactionId {
    return new TransactionId(value);
  }

  /**
   * Représentation textuelle
   */
  toString(): string {
    return this._value;
  }
}
